import React, { useState, useMemo } from 'react';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Upload,
  Download,
  Shield
} from 'lucide-react';
import EvidenceSummary from '../components/EvidenceSummary';
import EvidenceCheckTable from '../components/EvidenceCheckTable';
import EvidenceUploadModal from '../components/EvidenceUploadModal';
import EvidenceAttachmentCard from '../components/EvidenceAttachmentCard';
import {
  getEvidenceChecksForLevel,
  EVIDENCE_CHECKS
} from '../../config/cisaEvidenceModel';

/**
 * PillarEvidenceTab - Evidence-backed verification and reporting tab
 *
 * Redesigned to include:
 * - Evidence metrics dashboard
 * - Per-function verification checklists
 * - Evidence attachments collection
 * - Export functionality
 */
const PillarEvidenceTab = ({
  pillarCode,
  pillar,
  functions,
  assessments,
  targetLevels,
  productAdoptions,
  evidence = {},
  evidenceChecks = {},
  derivedScores = {},
  summary,
  onUpdateEvidenceCheck,
  onBulkUpdateEvidenceChecks,
  onAddEvidence,
  onUpdateEvidence,
  onRemoveEvidence
}) => {
  const [expandedFunction, setExpandedFunction] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadContext, setUploadContext] = useState(null);

  // Calculate evidence statistics for the pillar
  const evidenceStats = useMemo(() => {
    let totalRequiredChecks = 0;
    let verifiedChecks = 0;
    let partialChecks = 0;
    let missingChecks = 0;
    let totalEvidence = 0;
    let expiredEvidence = 0;
    let expiringEvidence = 0;

    for (const fn of functions) {
      const currentLevel = assessments[fn.code] || 'TRADITIONAL';
      const fnEvidence = evidence[fn.code] || [];
      const fnDerivedScore = derivedScores[fn.code];

      totalEvidence += fnEvidence.length;
      expiredEvidence += fnDerivedScore?.expiredEvidenceCount || 0;
      expiringEvidence += fnDerivedScore?.expiringEvidenceCount || 0;

      // Count checks for levels up to current
      const levels = ['INITIAL', 'ADVANCED', 'OPTIMAL'];
      const currentIdx = levels.indexOf(currentLevel);

      for (let i = 0; i <= currentIdx; i++) {
        const level = levels[i];
        const levelChecks = getEvidenceChecksForLevel(fn.code, level);
        const requiredLevelChecks = levelChecks.filter(c => c.type === 'REQUIRED');

        totalRequiredChecks += requiredLevelChecks.length;

        for (const check of requiredLevelChecks) {
          const status = evidenceChecks[check.id]?.status;
          if (status === 'VERIFIED') {
            verifiedChecks++;
          } else if (status === 'PARTIAL') {
            partialChecks++;
          } else {
            missingChecks++;
          }
        }
      }
    }

    const verificationRate = totalRequiredChecks > 0
      ? Math.round((verifiedChecks / totalRequiredChecks) * 100)
      : 100;

    return {
      totalRequiredChecks,
      verifiedChecks,
      partialChecks,
      missingChecks,
      totalEvidence,
      expiredEvidence,
      expiringEvidence,
      verificationRate
    };
  }, [functions, assessments, evidence, evidenceChecks, derivedScores]);

  // Build pillar-specific summary
  const pillarSummary = useMemo(() => {
    const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
    let functionsWithGaps = 0;
    let totalProductsAdopted = 0;
    const levelCounts = { TRADITIONAL: 0, INITIAL: 0, ADVANCED: 0, OPTIMAL: 0 };

    for (const fn of functions) {
      const currentLevel = assessments[fn.code] || 'TRADITIONAL';
      const targetLevel = targetLevels[fn.code];
      const products = productAdoptions[fn.code] || [];

      levelCounts[currentLevel]++;
      totalProductsAdopted += products.length;

      if (targetLevel && LEVELS.indexOf(targetLevel) > LEVELS.indexOf(currentLevel)) {
        functionsWithGaps++;
      }
    }

    return {
      ...summary,
      totalFunctions: functions.length,
      overallScore: summary.pillarScores?.[pillarCode] || 1.0,
      functionsWithGaps,
      totalProductsAdopted,
      levelCounts
    };
  }, [functions, assessments, targetLevels, productAdoptions, summary, pillarCode]);

  // Filter data to this pillar only
  const pillarData = useMemo(() => {
    const pillarAssessments = {};
    const pillarTargets = {};
    const pillarProducts = {};

    for (const fn of functions) {
      if (assessments[fn.code]) {
        pillarAssessments[fn.code] = assessments[fn.code];
      }
      if (targetLevels[fn.code]) {
        pillarTargets[fn.code] = targetLevels[fn.code];
      }
      if (productAdoptions[fn.code]) {
        pillarProducts[fn.code] = productAdoptions[fn.code];
      }
    }

    return { pillarAssessments, pillarTargets, pillarProducts };
  }, [functions, assessments, targetLevels, productAdoptions]);

  const handleOpenUploadModal = (functionCode, functionName, check) => {
    setUploadContext({
      functionCode,
      functionName,
      relatedCheck: check,
      adoptedProducts: productAdoptions[functionCode] || []
    });
    setUploadModalOpen(true);
  };

  const handleSubmitEvidence = (evidenceData) => {
    if (uploadContext) {
      onAddEvidence?.(uploadContext.functionCode, evidenceData);
    }
  };

  const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

  return (
    <div className="cisa-pillar-evidence">
      {/* Header */}
      <div className="cisa-pillar-evidence-header">
        <Shield size={20} style={{ color: pillar.color }} />
        <div>
          <h3>Evidence Verification</h3>
          <p>Validate your maturity assessment with evidence for {pillar.name}</p>
        </div>
      </div>

      {/* Evidence Metrics Dashboard */}
      <div className="cisa-pillar-evidence-metrics">
        <div className="cisa-pillar-evidence-metric">
          <div className="cisa-pillar-evidence-metric-icon cisa-metric-verified">
            <CheckCircle size={18} />
          </div>
          <div>
            <span className="cisa-pillar-evidence-metric-value">
              {evidenceStats.verificationRate}%
            </span>
            <span className="cisa-pillar-evidence-metric-label">Verified</span>
          </div>
        </div>
        <div className="cisa-pillar-evidence-metric">
          <span className="cisa-pillar-evidence-metric-value">
            {evidenceStats.verifiedChecks}
          </span>
          <span className="cisa-pillar-evidence-metric-label">
            / {evidenceStats.totalRequiredChecks} Checks
          </span>
        </div>
        <div className="cisa-pillar-evidence-metric">
          <span className="cisa-pillar-evidence-metric-value">
            {evidenceStats.totalEvidence}
          </span>
          <span className="cisa-pillar-evidence-metric-label">Evidence Items</span>
        </div>
        {evidenceStats.expiredEvidence > 0 && (
          <div className="cisa-pillar-evidence-metric cisa-metric-warning">
            <AlertTriangle size={16} />
            <span className="cisa-pillar-evidence-metric-value">
              {evidenceStats.expiredEvidence}
            </span>
            <span className="cisa-pillar-evidence-metric-label">Expired</span>
          </div>
        )}
        {evidenceStats.expiringEvidence > 0 && (
          <div className="cisa-pillar-evidence-metric cisa-metric-caution">
            <AlertTriangle size={16} />
            <span className="cisa-pillar-evidence-metric-value">
              {evidenceStats.expiringEvidence}
            </span>
            <span className="cisa-pillar-evidence-metric-label">Expiring Soon</span>
          </div>
        )}
      </div>

      {/* Function-by-Function Evidence */}
      <div className="cisa-pillar-evidence-functions">
        <h4>Function Verification Status</h4>
        {functions.map(fn => {
          const currentLevel = assessments[fn.code] || 'TRADITIONAL';
          const fnEvidence = evidence[fn.code] || [];
          const fnDerivedScore = derivedScores[fn.code];
          const isExpanded = expandedFunction === fn.code;

          // Count verified for this function
          const levels = ['INITIAL', 'ADVANCED', 'OPTIMAL'];
          const currentIdx = levels.indexOf(currentLevel);
          let fnRequiredChecks = 0;
          let fnVerifiedChecks = 0;

          for (let i = 0; i <= currentIdx; i++) {
            const level = levels[i];
            const levelChecks = getEvidenceChecksForLevel(fn.code, level);
            const requiredLevelChecks = levelChecks.filter(c => c.type === 'REQUIRED');
            fnRequiredChecks += requiredLevelChecks.length;
            fnVerifiedChecks += requiredLevelChecks.filter(
              c => evidenceChecks[c.id]?.status === 'VERIFIED'
            ).length;
          }

          const fnVerificationRate = fnRequiredChecks > 0
            ? Math.round((fnVerifiedChecks / fnRequiredChecks) * 100)
            : 100;

          return (
            <div key={fn.code} className="cisa-evidence-function-section">
              <button
                className="cisa-evidence-function-header"
                onClick={() => setExpandedFunction(isExpanded ? null : fn.code)}
              >
                <div className="cisa-evidence-function-title">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="cisa-evidence-function-name">{fn.name}</span>
                  <span className={`cisa-level-badge cisa-level--${currentLevel.toLowerCase()}`}>
                    {currentLevel}
                  </span>
                </div>
                <div className="cisa-evidence-function-stats">
                  {fnVerificationRate === 100 ? (
                    <span className="cisa-evidence-complete">
                      <CheckCircle size={14} />
                      Complete
                    </span>
                  ) : fnVerificationRate > 0 ? (
                    <span className="cisa-evidence-partial">
                      {fnVerifiedChecks}/{fnRequiredChecks} verified
                    </span>
                  ) : (
                    <span className="cisa-evidence-missing">
                      <XCircle size={14} />
                      Not started
                    </span>
                  )}
                  {fnEvidence.length > 0 && (
                    <span className="cisa-evidence-count">
                      <FileText size={12} />
                      {fnEvidence.length}
                    </span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="cisa-evidence-function-content">
                  {/* Evidence Check Table */}
                  <EvidenceCheckTable
                    functionCode={fn.code}
                    functionName={fn.name}
                    currentLevel={currentLevel}
                    evidenceChecks={evidenceChecks}
                    onUpdateCheck={onUpdateEvidenceCheck}
                    onBulkVerify={onBulkUpdateEvidenceChecks}
                    onUploadEvidence={(check) => handleOpenUploadModal(fn.code, fn.name, check)}
                  />

                  {/* Evidence Attachments */}
                  {fnEvidence.length > 0 && (
                    <div className="cisa-evidence-attachments">
                      <h5>
                        <FileText size={14} />
                        Evidence Attachments ({fnEvidence.length})
                      </h5>
                      <div className="cisa-evidence-attachments-grid">
                        {fnEvidence.map(item => (
                          <EvidenceAttachmentCard
                            key={item.id}
                            evidence={item}
                            compact
                            onEdit={(ev) => onUpdateEvidence?.(fn.code, ev.id, ev)}
                            onDelete={(ev) => onRemoveEvidence?.(fn.code, ev.id)}
                          />
                        ))}
                      </div>
                      <button
                        className="cisa-evidence-add-btn"
                        onClick={() => handleOpenUploadModal(fn.code, fn.name, null)}
                      >
                        <Upload size={14} />
                        Add Evidence
                      </button>
                    </div>
                  )}

                  {fnEvidence.length === 0 && (
                    <div className="cisa-evidence-empty">
                      <FileText size={24} />
                      <p>No evidence attachments yet</p>
                      <button
                        className="cisa-evidence-add-btn"
                        onClick={() => handleOpenUploadModal(fn.code, fn.name, null)}
                      >
                        <Upload size={14} />
                        Add First Evidence
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legacy: Level distribution */}
      <div className="cisa-pillar-evidence-distribution">
        <h4>Maturity Distribution</h4>
        <div className="cisa-pillar-evidence-levels">
          {LEVELS.map(level => {
            const count = pillarSummary.levelCounts[level];
            const percentage = functions.length > 0 ? (count / functions.length) * 100 : 0;

            return (
              <div key={level} className="cisa-pillar-evidence-level">
                <div className="cisa-pillar-evidence-level-info">
                  <span className={`cisa-pillar-evidence-level-name cisa-level--${level.toLowerCase()}`}>
                    {level.charAt(0) + level.slice(1).toLowerCase()}
                  </span>
                  <span className="cisa-pillar-evidence-level-count">
                    {count} ({Math.round(percentage)}%)
                  </span>
                </div>
                <div className="cisa-pillar-evidence-level-bar">
                  <div
                    className={`cisa-pillar-evidence-level-fill cisa-level--${level.toLowerCase()}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Evidence summary / export */}
      <EvidenceSummary
        assessments={pillarData.pillarAssessments}
        targetLevels={pillarData.pillarTargets}
        productAdoptions={pillarData.pillarProducts}
        summary={pillarSummary}
        pillarFilter={pillarCode}
      />

      {/* Upload Modal */}
      <EvidenceUploadModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadContext(null);
        }}
        onSubmit={handleSubmitEvidence}
        functionCode={uploadContext?.functionCode}
        functionName={uploadContext?.functionName}
        relatedCheck={uploadContext?.relatedCheck}
        adoptedProducts={uploadContext?.adoptedProducts || []}
      />
    </div>
  );
};

export default PillarEvidenceTab;
